import { memo } from "react";
import {
    Grid,
    FormControl,
    InputLabel,
    FilledInput,
    InputAdornment,
    Tooltip,
} from "@mui/material";

import useEntitlements from "@/auth/useEntitlements";
import { SectionTitle } from "@/common/typography";
import {
    FEATURE_ACCOUNTING_STRATEGY,
    FEATURE_RESOURCING_STRATEGY,
    FEATURE_BIDS,
    FEATURE_PROJECT_INTERNAL_BUDGET,
} from "@/constants";

const ProjectFinancialsSection = memo(function ProjectFinancialsSection({
    project,
    confidentialDataPermission,
    isMobile,
}) {
    const { hasFeature } = useEntitlements();

    return (
        <>
            <SectionTitle>Financials</SectionTitle>
            <Grid
                container
                spacing={2}
                sx={{ marginBottom: "48px" }}
                wrap={isMobile ? "wrap" : "nowrap"}
            >
                <Grid item flexGrow={1}>
                    <FormControl fullWidth variant="filled">
                        <InputLabel>Quoted</InputLabel>
                        <FilledInput
                            sx={{ borderRadius: "0px" }}
                            startAdornment={
                                <InputAdornment position="start">
                                    $
                                </InputAdornment>
                            }
                            value={
                                project.total_quoted
                                    ? project.total_quoted.toLocaleString()
                                    : 0
                            }
                            readOnly
                        />
                    </FormControl>
                </Grid>
                {confidentialDataPermission ? (
                    <>
                        <Grid item flexGrow={1}>
                            <FormControl fullWidth variant="filled">
                                <InputLabel>Hard Costs</InputLabel>
                                <FilledInput
                                    sx={{ borderRadius: "0px" }}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            $
                                        </InputAdornment>
                                    }
                                    value={
                                        project.external_budget
                                            ? project.external_budget.toLocaleString()
                                            : 0
                                    }
                                    readOnly
                                />
                            </FormControl>
                        </Grid>
                        <Grid item flexGrow={1}>
                            <FormControl fullWidth variant="filled">
                                <InputLabel>Gross Profit</InputLabel>
                                <FilledInput
                                    sx={{ borderRadius: "0px" }}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            $
                                        </InputAdornment>
                                    }
                                    value={
                                        project.gross_profit
                                            ? project.gross_profit.toLocaleString()
                                            : 0
                                    }
                                    readOnly
                                />
                            </FormControl>
                        </Grid>
                        <Grid item flexGrow={1}>
                            <FormControl fullWidth variant="filled">
                                <InputLabel>Running Total</InputLabel>
                                <FilledInput
                                    sx={{ borderRadius: "0px" }}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            $
                                        </InputAdornment>
                                    }
                                    value={
                                        project.running_total
                                            ? project.running_total.toLocaleString()
                                            : 0
                                    }
                                    readOnly
                                />
                            </FormControl>
                        </Grid>
                        {hasFeature(FEATURE_PROJECT_INTERNAL_BUDGET) && (
                            <Grid item flexGrow={1}>
                                <FormControl fullWidth variant="filled">
                                    <InputLabel>Internal Budget</InputLabel>
                                    <FilledInput
                                        sx={{ borderRadius: "0px" }}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        value={
                                            project.internal_budget
                                                ? project.internal_budget.toLocaleString()
                                                : 0
                                        }
                                        readOnly
                                    />
                                </FormControl>
                            </Grid>
                        )}

                        {hasFeature(FEATURE_RESOURCING_STRATEGY) && (
                            <Grid item flexGrow={1}>
                                <FormControl fullWidth variant="filled">
                                    <InputLabel>Internal Actual</InputLabel>
                                    <FilledInput
                                        sx={
                                            project.float_internal_scheduled >
                                            project.internal_budget
                                                ? {
                                                      borderRadius: "0px",
                                                      "::before": {
                                                          content: "none",
                                                      },
                                                      "::after": {
                                                          content: "none",
                                                      },
                                                      borderBottom:
                                                          "red solid 1px",
                                                  }
                                                : { borderRadius: "0px" }
                                        }
                                        startAdornment={
                                            <InputAdornment position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        value={
                                            project.float_internal_scheduled
                                                ? project.float_internal_scheduled.toLocaleString()
                                                : 0
                                        }
                                        readOnly
                                    />
                                </FormControl>
                            </Grid>
                        )}
                    </>
                ) : null}

                {hasFeature(FEATURE_ACCOUNTING_STRATEGY) && (
                    <>
                        <Tooltip title="* old & new numbers" placement="top">
                            <Grid item flexGrow={1}>
                                <FormControl fullWidth variant="filled">
                                    <InputLabel>
                                        Billed{" "}
                                        {
                                            <span
                                                style={{
                                                    fontSize: 14,
                                                    paddingLeft: 3,
                                                }}
                                            >
                                                {project.accounting_rev_tmp.toLocaleString()}{" "}
                                                *
                                            </span>
                                        }{" "}
                                    </InputLabel>
                                    <FilledInput
                                        sx={{ borderRadius: "0px" }}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        value={
                                            project.accounting_rev
                                                ? project.accounting_rev.toLocaleString()
                                                : 0
                                        }
                                        readOnly
                                    />
                                </FormControl>
                            </Grid>
                        </Tooltip>
                        <Tooltip title="* old & new numbers" placement="top">
                            {confidentialDataPermission ? (
                                <Grid item flexGrow={1}>
                                    <FormControl fullWidth variant="filled">
                                        <InputLabel>
                                            Spent{" "}
                                            {
                                                <span
                                                    style={{
                                                        fontSize: 14,
                                                        paddingLeft: 3,
                                                    }}
                                                >
                                                    {project.accounting_cost_tmp.toLocaleString()}{" "}
                                                    *
                                                </span>
                                            }
                                        </InputLabel>
                                        <FilledInput
                                            sx={{ borderRadius: "0px" }}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    $
                                                </InputAdornment>
                                            }
                                            value={
                                                project.accounting_cost
                                                    ? project.accounting_cost.toLocaleString()
                                                    : 0
                                            }
                                            readOnly
                                        />
                                    </FormControl>
                                </Grid>
                            ) : null}
                        </Tooltip>
                    </>
                )}
            </Grid>
        </>
    );
});

ProjectFinancialsSection.displayName = "ProjectFinancialsSection";

export default ProjectFinancialsSection;
